import { useLazyQuery, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { IonReorder, IonReorderGroup, ItemReorderEventDetail } from "@ionic/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import ActionIconButton from "@components/buttons/ActionIconButton";
import TertiaryButton from "@components/buttons/TertiaryButton";
import BaseErrors from "@components/fields/BaseErrors";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import ResourceFeatureTemplateSelect from "@components/fields/ResourceFeatureTemplateSelect";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import styles from "@components/forms/TaskResourceFeaturesForm.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import {
  GetResourceFeatureTemplateDocument,
  GetResourceFeatureTemplateQuery,
  GetResourceFeatureTemplateQueryVariables,
  ResourceFeatureKindEnum,
  TaskFragment as Task,
  TaskUpdateDocument,
  TaskUpdateMutation,
  TaskUpdateMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { sortByPosition } from "@utils/modelUtils";

type ResourceFeature = {
  featureId: string | null;
  kind: ResourceFeatureKindEnum;
  name: string;
};

type FormData = {
  resourceFeatures: ResourceFeature[];
};

type ResourceFeatureTemplateFormData = {
  resourceFeatureTemplateId: string;
};

type Props = {
  onCancel: () => void;
  onSuccess: () => void;
  task: Task;
};

const TaskResourceFeaturesForm = ({ onCancel, onSuccess, task }: Props) => {
  const existingFeatures: ResourceFeature[] = useMemo(
    () =>
      sortByPosition(task.resourceFeatures).map(feature => ({
        featureId: feature.id,
        kind: feature.kind,
        name: feature.name
      })),
    [task.resourceFeatures]
  );

  const calculateDefaultValues = useCallback(
    (): FormData => ({
      resourceFeatures: existingFeatures ?? []
    }),
    [existingFeatures]
  );

  const defaultValues = calculateDefaultValues();

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateTask] = useMutation<TaskUpdateMutation, TaskUpdateMutationVariables>(TaskUpdateDocument);

  const [getResourceFeatureTemplate] = useLazyQuery<
    GetResourceFeatureTemplateQuery,
    GetResourceFeatureTemplateQueryVariables
  >(GetResourceFeatureTemplateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      resourceFeatures: yup
        .array()
        .defined()
        .of(
          yup.object().shape({
            featureId: yup.string().nullable().defined(),
            kind: yup
              .string()
              .oneOf(Object.values(ResourceFeatureKindEnum))
              .required(intl.formatMessage({ id: "errors.taskResourceFeatures.kind.blank" })),
            name: yup.string().required(intl.formatMessage({ id: "errors.taskResourceFeatures.name.blank" }))
          })
        )
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    form.reset(calculateDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateDefaultValues]);

  const resourceFeatureTemplateSchema = yup
    .object()
    .defined()
    .shape({ resourceFeatureTemplateId: yup.string().defined() })
    .required();

  const resourceFeatureTemplateForm = useForm<ResourceFeatureTemplateFormData>({
    defaultValues: { resourceFeatureTemplateId: undefined },
    mode: "onChange",
    resolver: yupResolver(resourceFeatureTemplateSchema)
  });

  const resourceFeatureTemplateId = resourceFeatureTemplateForm.watch("resourceFeatureTemplateId");

  const {
    append: resourceFeatureAppend,
    fields: resourceFeatureFields,
    move: resourceFeatureMove,
    remove: resourceFeatureRemove
  } = useFieldArray<FormData>({
    control: form.control,
    name: "resourceFeatures"
  });

  useEffect(() => {
    if (resourceFeatureTemplateId) {
      getResourceFeatureTemplate({ variables: { id: resourceFeatureTemplateId } }).then(response => {
        const features = response.data?.resourceFeatureTemplate.resourceFeatures;
        if (features) {
          sortByPosition(features).forEach(feature => {
            resourceFeatureAppend({ featureId: null, kind: feature.kind, name: feature.name });
          });
        }
      });
    }
  }, [getResourceFeatureTemplate, resourceFeatureAppend, resourceFeatureTemplateId, task.id]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    const mappedFeatures = form.getValues().resourceFeatures.map(rf => ({
      id: rf.featureId,
      kind: rf.kind,
      name: rf.name
    }));

    return updateTask({
      variables: {
        id: task.id,
        journeyId: task.journey?.id,
        resourceFeatures: mappedFeatures
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.taskUpdate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.taskResourceFeatures"
      });
    });
  }, [form, intl, onSuccess, task.id, task.journey?.id, updateTask]);

  const kindOptions = [
    {
      label: intl.formatMessage({ id: "forms.resourceFeature.kinds.boolean" }),
      value: ResourceFeatureKindEnum.BOOLEAN
    },
    {
      label: intl.formatMessage({ id: "forms.resourceFeature.kinds.text" }),
      value: ResourceFeatureKindEnum.TEXT
    }
  ];

  const reorderFeatures = useCallback(
    (event: CustomEvent<ItemReorderEventDetail>) => {
      resourceFeatureMove(event.detail.from, event.detail.to);
      event.detail.complete();
    },
    [resourceFeatureMove]
  );

  return (
    <>
      <div>
        <BodyCopy>
          <FormattedMessage id="forms.taskResourceFeatures.explanation" />
        </BodyCopy>
      </div>
      <Form
        hookForm={resourceFeatureTemplateForm}
        onSubmit={resourceFeatureTemplateForm.handleSubmit(async () => onSubmit())}
      >
        <ResourceFeatureTemplateSelect<ResourceFeatureTemplateFormData>
          control={resourceFeatureTemplateForm.control}
          error={resourceFeatureTemplateForm.formState.errors.resourceFeatureTemplateId}
          initialOption={{ label: undefined, value: null }}
          label={intl.formatMessage({ id: "forms.resourceFeature.resourceFeatureTemplateIdLabel" })}
          name="resourceFeatureTemplateId"
        />
      </Form>
      <Form hookForm={form} onSubmit={form.handleSubmit(async () => onSubmit())}>
        <BaseErrors errors={baseErrors} />
        <IonReorderGroup disabled={false} onIonItemReorder={reorderFeatures}>
          {resourceFeatureFields.map((field, index) => (
            <div className={styles.resourceFeatureRow} key={field.id}>
              <div className={styles.resourceFeatureRowReorder}>
                <IonReorder slot="start" />
              </div>
              <div className={styles.resourceFeatureRowControl}>
                <Select<FormData>
                  control={form.control}
                  disabled={task.resourceFeatures.some(rf => rf.id === field.featureId)}
                  error={form.formState.errors.resourceFeatures?.[index]?.kind}
                  label={intl.formatMessage({ id: "forms.resourceFeature.kindLabel" })}
                  name={`resourceFeatures.${index}.kind`}
                  options={kindOptions}
                  required
                />
              </div>
              <div className={styles.resourceFeatureRowControl}>
                <div className="ion-margin-start">
                  <Input<FormData>
                    control={form.control}
                    error={form.formState.errors.resourceFeatures?.[index]?.name}
                    label={intl.formatMessage({ id: "forms.resourceFeature.nameLabel" })}
                    name={`resourceFeatures.${index}.name`}
                    required
                    spellcheck
                  />
                </div>
              </div>
              <div>
                <div className={`ion-margin-start ${styles.deleteIcon}`}>
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.delete"
                    icon="times"
                    onClick={() => resourceFeatureRemove(index)}
                  />
                </div>
              </div>
            </div>
          ))}
        </IonReorderGroup>
        <div className="ion-margin-top">
          <TertiaryButton
            icon="add"
            onClick={() => resourceFeatureAppend({ featureId: null, kind: ResourceFeatureKindEnum.TEXT, name: "" })}
            textKey="forms.resourceFeature.addResourceFeature"
          />
        </div>
        <FormButtons hookForm={form} onCancel={onCancel} />
      </Form>
    </>
  );
};

export default TaskResourceFeaturesForm;
